import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Check, Eye, Loader2, Sparkles, Upload, X } from "lucide-react";
import { FilePreviewDialog } from "@/components/FilePreviewDialog";
import { legalPreviewFile } from "@/components/legal/DocumentVault";
import { toast } from "sonner";

const FIELD_LABELS = { denomination: "Dénomination", forme_juridique: "Forme juridique", capital_social: "Capital social", siren: "SIREN",
  siret: "SIRET", rcs_ville: "RCS", numero_tva: "N° TVA", siege_adresse: "Siège", date_creation: "Date de création",
  date_cloture_exercice: "Clôture exercice", representant_legal: "Représentant légal", representant_titre: "Titre",
  activite_principale: "Activité", code_ape: "Code APE" };
const STEPS = ["Classification", "Extraction", "Validation"];

const Stepper = ({ step }) => (
  <div className="flex items-center gap-2 text-xs">
    {STEPS.map((s, i) => (
      <div key={s} className="flex items-center gap-2">
        <span className={`w-5 h-5 rounded-full flex items-center justify-center font-semibold ${i < step ? "bg-emerald-500 text-white" : i === step ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-500"}`}>{i < step ? <Check className="w-3 h-3" /> : i + 1}</span>
        <span className={i === step ? "font-semibold" : "text-slate-500"}>{s}</span>
        {i < 2 && <span className="w-6 h-px bg-slate-300" />}
      </div>
    ))}
  </div>
);

const Row = ({ checked, onChange, children, testid }) => (
  <label className="flex items-start gap-2 p-2 rounded border bg-white text-sm cursor-pointer hover:bg-slate-50" data-testid={testid}>
    <Checkbox checked={checked} onCheckedChange={onChange} className="mt-0.5" />
    <div className="flex-1">{children}</div>
  </label>
);

const toggle = (set, i) => { const n = new Set(set); if (n.has(i)) { n.delete(i); } else { n.add(i); } return n; };

const Review = ({ job, onDone }) => {
  const [fields, setFields] = useState(new Set(job.profile_diffs.filter((d) => d.changed && !d.current).map((d) => d.field)));
  const [entries, setEntries] = useState(new Set(job.register_entries.map((_, i) => i)));
  const [dirs, setDirs] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const act = async (validate) => {
    setBusy(true);
    try {
      if (validate) {
        const r = await api.post(`/legal/import/jobs/${job.id}/validate`, { profile_fields: [...fields], entry_indexes: [...entries], dirigeant_indexes: [...dirs] });
        const a = r.data.applied;
        toast.success(`Validé : ${a.profile.length} champ(s) du profil, ${a.entries} inscription(s), ${a.dirigeants} dirigeant(s)`);
      } else { await api.post(`/legal/import/jobs/${job.id}/reject`); toast.info("Import ignoré (le document reste dans le coffre-fort)"); }
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="import-review">
      <div className="p-3 rounded-md bg-blue-50 border border-blue-100 text-sm">
        <div className="flex items-center gap-2 flex-wrap"><Badge className="bg-blue-600">{job.document_label || job.document_type}</Badge>
          <span className="text-xs text-slate-500">confiance {job.confidence ?? "?"}% · {job.filename}</span></div>
        <p className="mt-1 text-slate-700">{job.summary}</p>
      </div>
      {job.profile_diffs.length > 0 && <div className="space-y-1"><p className="text-xs font-semibold uppercase text-slate-500">Profil société</p>
        {job.profile_diffs.map((d) => (
          <Row key={d.field} checked={fields.has(d.field)} onChange={() => setFields(toggle(fields, d.field))} testid={`import-field-${d.field}`}>
            <b>{FIELD_LABELS[d.field] || d.field}</b> : <span className="text-emerald-700">{String(d.proposed)}</span>
            {d.current ? (d.changed ? <span className="text-xs text-amber-700"> · actuel : {String(d.current)}</span> : <span className="text-xs text-slate-400"> · identique</span>) : <span className="text-xs text-slate-400"> · champ vide</span>}
          </Row>))}</div>}
      {job.register_entries.length > 0 && <div className="space-y-1"><p className="text-xs font-semibold uppercase text-slate-500">Inscriptions aux registres</p>
        {job.register_entries.map((e, i) => (
          <Row key={i} checked={entries.has(i)} onChange={() => setEntries(toggle(entries, i))} testid={`import-entry-${i}`}>
            <span className="text-xs font-mono text-slate-500">{e.date_operation} · {e.type}</span><p className="font-medium">{e.titre}</p><p className="text-xs text-slate-600">{e.description}</p>
          </Row>))}</div>}
      {job.dirigeants.length > 0 && <div className="space-y-1"><p className="text-xs font-semibold uppercase text-slate-500">Dirigeants détectés (à ajouter)</p>
        {job.dirigeants.map((d, i) => (
          <Row key={i} checked={dirs.has(i)} onChange={() => setDirs(toggle(dirs, i))} testid={`import-dirigeant-${i}`}>
            {d.prenom} {d.nom} — {d.fonction} {d.date_nomination && <span className="text-xs text-slate-500">· nommé le {d.date_nomination}</span>}
          </Row>))}</div>}
      <div className="flex justify-end gap-2">
        <Button variant="outline" disabled={busy} onClick={() => act(false)} data-testid="import-reject-btn"><X className="w-4 h-4 mr-1" />Ignorer</Button>
        <Button disabled={busy} onClick={() => act(true)} data-testid="import-validate-btn">{busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}Valider la sélection</Button>
      </div>
    </div>
  );
};

const PIPE = ["Uploadé", "Classifié", "Extrait", "Validé"];
const JobsList = ({ jobs, onOpen, onView }) => (
  <div className="divide-y border rounded-lg bg-white" data-testid="import-jobs-list">
    {jobs.map((j) => {
      const done = j.status === "validated" ? 4 : j.status === "rejected" ? 3 : 3;
      return (
        <div key={j.id} className="p-2.5 flex items-center gap-3 text-sm flex-wrap" data-testid={`import-job-${j.id}`}>
          <span className="flex-1 min-w-[160px] truncate">{j.filename} <span className="text-xs text-slate-500">· {j.document_label || j.document_type}</span></span>
          <div className="flex items-center gap-1">
            {PIPE.map((p, i) => (
              <span key={p} className={`text-[10px] px-1.5 py-0.5 rounded ${i < done ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>{p}</span>
            ))}
            {j.status === "rejected" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">Ignoré</span>}
          </div>
          {j.document_id && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onView({ id: j.document_id, filename: j.filename })} data-testid={`import-job-view-${j.id}`}><Eye className="w-3.5 h-3.5 mr-1" />Voir</Button>}
          {j.status === "to_validate" && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onOpen(j)} data-testid={`import-job-open-${j.id}`}>Valider</Button>}
        </div>
      );
    })}
  </div>
);

export const LegalImportWizard = ({ onApplied }) => {
  const [job, setJob] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const [preview, setPreview] = useState(null);
  const loadJobs = () => api.get("/legal/import/jobs").then((r) => setJobs(r.data.jobs)).catch(() => {});
  useEffect(() => {
    api.get("/legal/import/jobs").then((r) => { setJobs(r.data.jobs); setJob(r.data.jobs.find((j) => j.status === "to_validate") || null); }).catch(() => {});
  }, []);
  const upload = async (file) => {
    if (!file) return;
    setLoading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/legal/import/analyze", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 240000 });
      setJob(r.data); loadJobs();
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <Card className="border-purple-200" data-testid="legal-import-wizard">
      <CardHeader className="pb-2 flex flex-row items-center justify-between flex-wrap gap-2 space-y-0">
        <CardTitle className="text-base flex items-center gap-2"><Sparkles className="w-4 h-4 text-purple-600" />Import intelligent de documents</CardTitle>
        <Stepper step={job ? 2 : loading ? 1 : 0} />
      </CardHeader>
      <CardContent>
        {job ? <Review job={job} onDone={() => { setJob(null); loadJobs(); onApplied(); }} /> : (
          <div className="flex flex-col items-center gap-2 p-6 rounded-xl border-2 border-dashed border-purple-200 bg-purple-50/40 text-center transition-colors"
            onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("bg-purple-100"); }}
            onDragLeave={(e) => e.currentTarget.classList.remove("bg-purple-100")}
            onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove("bg-purple-100"); upload(e.dataTransfer.files?.[0]); }}
            data-testid="import-dropzone">
            <Upload className="w-7 h-7 text-purple-500" />
            <p className="text-sm font-medium">Glisse ici un document (statuts, Kbis, PV, cession…)</p>
            <Button onClick={() => ref.current?.click()} disabled={loading} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="import-upload-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
              {loading ? "L'IA lit le document…" : "ou choisis un fichier"}</Button>
            <input ref={ref} type="file" accept=".pdf,.docx,.png,.jpg,.jpeg" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="import-upload-input" />
            <p className="text-xs text-slate-500 max-w-xl">L'IA reconnaît le document, propose les mises à jour (société, registres, dirigeants) que tu valides en un clic, et le range chiffré dans tes documents.</p>
          </div>
        )}
        {jobs.length > 0 && <div className="mt-4 space-y-1"><p className="text-xs font-semibold uppercase text-slate-500">Documents importés</p><JobsList jobs={jobs} onOpen={setJob} onView={setPreview} /></div>}
        <FilePreviewDialog file={legalPreviewFile(preview)} onClose={() => setPreview(null)} />
      </CardContent>
    </Card>
  );
};
